import React, { useEffect, useState } from 'react'
import {
  Add,
  Delete,
  Edit,
  EditRounded,
  Apartment,
  Language,
  InfoOutlined
} from '@material-ui/icons'
import {
  Typography,
  Card,
  makeStyles,
  IconButton,
  Button,
  Tooltip,
  Container,
  Avatar,
  CircularProgress,
  Link,
  TableCell,
  Chip
} from '@material-ui/core'
import { Box, TableContainer, TableBody, TableRow, TableHead, Table, Paper } from '@mui/material'
import { useHistory, useLocation } from 'react-router-dom'
import talkSlice, {
  deleteTalkAlongPoll,
  fetchConferenceTalksAlongData,
  fetchSubjectsTalks
} from 'store/talk'
import { useDispatch, useSelector } from 'react-redux'
import ConfirmModal from 'components/ConfirmationModal'
import { toast } from 'react-toastify'
import ResourceNotFound from 'components/ResourceNotFound'
import { isValidLink } from 'utils'
import { storageRef } from 'index'
import LogoWithConference from 'components/LogoWithConference'
import { getTalkStatusText } from 'utils/talk'
import { fetchSettings } from 'store/settings'
import RichTextRenderer from 'components/RichTextRenderer'

export const Talks = ({ forApproval, subjectId, confId }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const classes = useStyles()
  const token = new URLSearchParams(location.search).get('token')
  const [deletedTalkId, setDeletedTalkId] = useState('')
  const [loading, setLoading] = useState(false)
  const { talks, inProgress } = useSelector((state) => state.talk)

  useEffect(() => {
    if (subjectId) {
      forApproval
        ? dispatch(fetchConferenceTalksAlongData(subjectId, confId))
        : dispatch(fetchSubjectsTalks(subjectId))
    }
    return () => dispatch(talkSlice.actions.setTalks([]))
  }, [dispatch, subjectId])
  const handleCloseDeleteModal = () => {
    setDeletedTalkId('')
  }

  const handleDeleteTalk = async () => {
    setLoading(true)
    await dispatch(deleteTalkAlongPoll(deletedTalkId, { forApproval }))
    toast.success('Talk deleted successfully.')
    setLoading(false)
  }

  const handleEditTalk = (talkId) => {
    history.push(
      forApproval
        ? `/admin/subjects/edit/${subjectId}/talks/${talkId}`
        : `/subjects/speaker/talks/edit/${talkId}?token=${token}`
    )
  }

  return (
    <>
      <Box className={classes.talks}>
        {inProgress ? (
          <Box sx={{ display: 'flex', padding: '20px', justifyContent: 'center' }}>
            <CircularProgress size={27} />
          </Box>
        ) : !talks.length ? (
          <ResourceNotFound message="No talks available" />
        ) : (
          <Box>
            {forApproval ? (
              <Paper sx={{ overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: '500px' }}>
                  <Table aria-label="sticky table">
                    <TableHead className={classes.stickyHeader}>
                      <TableRow className={classes.tableRow}>
                        <TableCell className={classes.tableHead}>Talk Title</TableCell>
                        <TableCell className={classes.tableHead}>Event</TableCell>
                        <TableCell className={classes.tableHead}>Session</TableCell>
                        <TableCell className={classes.tableHead}>Status</TableCell>
                        <TableCell className={classes.bold} style={{ minWidth: '110px' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {talks?.map((talk) => {
                        const { status, color, bgColor } = getTalkStatusText(talk?.status)
                        return (
                          <TableRow key={talk.id}>
                            <TableCell className={classes.rightBorder}>{talk.title}</TableCell>
                            <TableCell className={classes.rightBorder}>
                              <Box component="span">
                                <Typography variant="body2">{talk?.eventTitle ?? 'N/A'}</Typography>
                                {talk?.eventStartTime && (
                                  <Typography variant="body2" className={classes.subText}>
                                    {talk?.eventStartTime} - {talk?.eventEndTime}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell className={classes.rightBorder}>
                              <Box component="span">
                                <Typography variant="body2">
                                  {talk?.sessionTitle ?? 'N/A'}
                                </Typography>
                                {talk?.sessionStartTime && (
                                  <Typography variant="body2" className={classes.subText}>
                                    {talk?.sessionStartTime} - {talk?.sessionEndTime}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell className={classes.rightBorder}>
                              <Chip
                                size="small"
                                label={status}
                                style={{ backgroundColor: bgColor, color }}
                              />
                            </TableCell>
                            <TableCell style={{ minWidth: '110px' }}>
                              <Box component="span">
                                <Tooltip title="Edit">
                                  <IconButton
                                    className={classes.iconButton}
                                    onClick={() => handleEditTalk(talk?.id)}>
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete" onClick={() => setDeletedTalkId(talk.id)}>
                                  <IconButton className={classes.iconButton}>
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              <Box className={classes.talkCardList}>
                {talks?.map((talk) => (
                  <Card className={classes.talkCard} key={talk.id}>
                    <Typography>{talk?.title}</Typography>
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => handleEditTalk(talk?.id)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" onClick={() => setDeletedTalkId(talk.id)}>
                        <IconButton className={classes.iconButton}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <ConfirmModal
        dialogTitle="Delete Talk"
        dialogText="Do you really want to delete this talk?"
        open={Boolean(deletedTalkId)}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeleteTalk}
        inProgress={loading}
      />
    </>
  )
}

const TalksListing = ({ onAction }) => {
  const history = useHistory()
  const location = useLocation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const token = new URLSearchParams(location.search).get('token')
  const { subject } = useSelector((state) => state.subjects)
  const { conference } = useSelector((state) => state.conference)
  const [speakerProfile, setSpeakerProfile] = useState(null)
  const { talks } = useSelector((state) => state.talk)

  const downloadImage = async (imageName) => {
    const imageUrl = await storageRef.child(`subjects/${imageName}`).getDownloadURL()
    setSpeakerProfile(imageUrl)
  }

  useEffect(() => {
    if (subject?.imageName) {
      downloadImage(subject?.imageName)
    }
  }, [subject?.imageName])

  useEffect(() => {
    dispatch(fetchSettings(conference?.id))
  }, [conference?.id])

  useEffect(() => {
    if (subject?.id) {
      dispatch(fetchSubjectsTalks(subject?.id))
    }
    return () => dispatch(talkSlice.actions.setTalks([]))
  }, [dispatch, subject?.id])

  return (
    <Container className={classes.container}>
      <LogoWithConference confName={conference?.confName} />
      <Card className={classes.card} elevation={0}>
        <Box className={classes.infoBox}>
          <Typography className={classes.title}>Respected Speaker,</Typography>
          {talks?.length ? (
            <Typography className={classes.subTitle}>
              Thank you for completing your profile and creating your talks. If you&apos;d like to
              make updates to your profile or add/edit talks, you can do so by clicking the{' '}
              <span className={classes.bold}> Edit Profile</span> or{' '}
              <span className={classes.bold}> Create Talk</span> button below.
            </Typography>
          ) : (
            <>
              <Typography className={classes.subTitle}>
                Thank you for completing your profile. You can edit your profile by clicking
                <span className={classes.bold}> Edit Profile</span> button below.
              </Typography>
              {conference?.speakerCanUploadTalks && (
                <Typography className={classes.subtitleText}>
                  To share your valuable insights at conference, kindly spare a few minutes and
                  create your talks by clicking <span className={classes.bold}>Create Talk</span>{' '}
                  button below.
                </Typography>
              )}
            </>
          )}
        </Box>

        <Box className={classes.header}>
          <Typography variant="h5">Speaker Profile</Typography>
          <Button
            startIcon={<EditRounded />}
            variant="contained"
            color="primary"
            onClick={onAction}>
            Edit Profile
          </Button>
        </Box>

        <Card elevation={1} className={classes.profileCard}>
          <Avatar
            alt="Remy Sharp"
            variant="square"
            src={speakerProfile}
            className={classes.profile}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Box>
              <Typography className={classes.name}>{subject?.fullName}</Typography>
              <Typography className={classes.email}>{subject?.email}</Typography>
            </Box>
            <Box className={classes.singleInfo}>
              <Apartment fontSize="small" color="action" />
              <Typography variant="body2">
                {subject?.company || 'N/A'} {subject?.position && `- ${subject?.position}`}
              </Typography>
            </Box>
            <Box className={classes.singleInfo}>
              <Language title="website url" fontSize="small" color="action" />
              {isValidLink(subject?.websiteUrl) ? (
                <Link underline="hover" href={subject?.websiteUrl} target="_blank" variant="body2">
                  {subject?.websiteUrl}
                </Link>
              ) : (
                <Typography variant="body2">{subject?.websiteUrl || 'N/A'}</Typography>
              )}
            </Box>

            <Box className={classes.bio}>
              <InfoOutlined fontSize="small" color="action" />
              <RichTextRenderer htmlContent={subject?.bio || 'N/A'} />
            </Box>
          </Box>
        </Card>

        {conference?.speakerCanUploadTalks ? (
          <>
            <Box className={classes.header} style={{ width: '55%', marginBottom: '40px' }}>
              <Typography variant="h5">Speaker Talks</Typography>
              <Button
                className={classes.orangeBtn}
                variant="contained"
                startIcon={<Add />}
                onClick={() => history.push(`/subjects/speaker/talks/new?token=${token}`)}>
                Create Talk
              </Button>
            </Box>
            <Talks forApproval={false} subjectId={subject?.id} confId={conference?.id} />
          </>
        ) : null}
      </Card>
    </Container>
  )
}

export default TalksListing

const useStyles = makeStyles({
  container: {
    paddingBottom: '1.5rem',
    paddingTop: '1.5rem'
  },
  card: {
    padding: '1rem',
    border: '1px solid #e0e0e0'
  },
  talkCard: {
    padding: '10px 30px',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  iconButton: {
    padding: '8px'
  },
  talks: { marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  infoBox: {
    padding: '16px',
    borderRadius: '8px',
    backgroundColor: '#e0f7fa'
  },
  title: {
    fontSize: '16px',
    fontWeight: 600
  },
  subTitle: {
    fontSize: '16px',
    marginTop: '6px'
  },
  subtitleText: {
    fontSize: '16px',
    marginTop: '10px'
  },
  bio: {
    display: 'flex',
    alignItems: 'start',
    gap: '1rem'
  },
  singleInfo: { display: 'flex', alignItems: 'center', gap: '1rem' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px'
  },
  profileCard: {
    marginTop: '1rem',
    padding: '1rem',
    display: 'flex',
    gap: '2.5rem'
  },
  profile: {
    height: '140px',
    width: '150px'
  },
  shortText: {
    display: 'inline-block',
    width: '300px',
    whiteSpace: 'nowrap',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis'
  },
  name: {
    fontSize: '18px',
    fontWeight: 600
  },
  email: {
    fontSize: '14px',
    fontWeight: 500
  },
  bold: {
    fontWeight: 'bold'
  },
  orangeBtn: {
    color: 'white',
    backgroundColor: '#fb8c00',
    '&:hover': {
      backgroundColor: '#ef6c00'
    }
  },
  tableRow: {
    backgroundColor: '#e0e0e0'
  },
  tableHead: {
    fontWeight: 'bold',
    borderRight: '1px solid #bdbdbd'
  },
  rightBorder: {
    borderRight: '1px solid #e0e0e0'
  },
  subText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#757575'
  },
  stickyHeader: {
    paddingTop: '20px',
    paddingBottom: '10px',
    position: 'sticky',
    marginBottom: '16px',
    background: 'white',
    top: '0px',
    zIndex: 999
  },
  talkCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  }
})
